import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import api from '../services/apiService';
import ReactMarkdown from 'react-markdown';
import {
  BookOpen,
  Plus,
  Search,
  Edit,
  Trash2,
  Save,
  X,
  Star,
  Calendar,
  Loader,
  AlertCircle,
  ArrowLeft,
  BarChart3
} from 'lucide-react';
import './NotesTab.css';
import CoachNotesStatistics from './CoachNotesStatistics';

// NoteForm sub-component for create/edit - defined outside to prevent re-creation
const NoteForm = ({ mode, formData, onFormChange, onSave, onMemorize, onCancel, loading, error, successMessage }) => {
  const categories = [
    'Technical Skills',
    'Choreography/Artistry',
    'Mental/Performance',
    'Physical Conditioning',
    'Competition Prep',
    'General Feedback'
  ];

  const validateForm = () => {
    if (!formData.title.trim()) {
      return 'Title is required';
    }
    if (!formData.content.trim()) {
      return 'Note content is required';
    }
    if (!formData.date) {
      return 'Date is required';
    }
    return null;
  };

  const handleSave = () => {
    const validationError = validateForm();
    if (validationError) {
      return;
    }
    onSave();
  };

  const handleMemorize = () => {
    const validationError = validateForm();
    if (validationError) {
      return;
    }
    onMemorize();
  };

  return (
    <div className="note-form-view">
      <div className="form-header">
        <button onClick={onCancel} className="back-button">
          <ArrowLeft size={18} />
          Back to Notes
        </button>
        <h2>{mode === 'create' ? 'Create Coach Note' : 'Edit Coach Note'}</h2>
      </div>

      <div className="note-form">
        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="success-message">
            <Save size={16} />
            {successMessage}
          </div>
        )}

        <div className="form-group">
          <label>Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => onFormChange({ ...formData, title: e.target.value })}
            placeholder="e.g., Working on 3Lz technique"
            className="input-field"
            maxLength={255}
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Date *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => onFormChange({ ...formData, date: e.target.value })}
              className="input-field"
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          <div className="form-group">
            <label>Category *</label>
            <select
              value={formData.category}
              onChange={(e) => onFormChange({ ...formData, category: e.target.value })}
              className="input-field"
              required
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Note Content *</label>
          <textarea
            value={formData.content}
            onChange={(e) => onFormChange({ ...formData, content: e.target.value })}
            placeholder="Enter your coach's feedback and notes..."
            className="textarea-field"
            rows={12}
            required
          />
        </div>

        <div className="form-actions">
          <button
            onClick={onCancel}
            className="action-button secondary"
            disabled={loading}
          >
            <X size={16} />
            Cancel
          </button>
          <button
            onClick={handleMemorize}
            className="action-button memorize-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className="spinner" size={16} />
                Saving...
              </>
            ) : (
              <>
                <Star size={16} />
                Memorize
              </>
            )}
          </button>
          <button
            onClick={handleSave}
            className="action-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader className="spinner" size={16} />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const NotesTab = () => {
  // State management
  const [view, setView] = useState('list'); // 'list', 'create', 'edit', 'detail'
  const [notes, setNotes] = useState([]);
  const [currentNote, setCurrentNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = most recent first
  const [showStatistics, setShowStatistics] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const NOTES_PER_PAGE = 20;

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    category: 'Technical Skills',
    content: ''
  });

  // Ref to track timeout IDs for cleanup
  const timeoutRefs = useRef([]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutRefs.current.forEach(id => clearTimeout(id));
    };
  }, []);

  // Load notes on mount
  useEffect(() => {
    if (view === 'list') {
      loadNotes(true); // Reset on view change
    }
  }, [view]);

  const loadNotes = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setOffset(0);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const currentOffset = reset ? 0 : offset;
      const response = await api.get(`/api/notes?limit=${NOTES_PER_PAGE}&offset=${currentOffset}`);
      // Backend returns { notes: [...], total: N, success: true }
      if (response.data && Array.isArray(response.data.notes)) {
        const newNotes = response.data.notes;
        if (reset) {
          setNotes(newNotes);
        } else {
          setNotes(prev => [...prev, ...newNotes]);
        }
        setOffset(currentOffset + newNotes.length);
        setHasMore(newNotes.length === NOTES_PER_PAGE);
      } else {
        if (reset) setNotes([]);
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading notes:', err);
      setError(err.response?.data?.detail || 'Failed to load notes');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const createNote = async (memorize = false) => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and content are required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.post(`/api/notes`, formData);
      const newNote = response.data;

      // If memorize is true, also memorize the note
      if (memorize) {
        await api.post(`/api/notes/${newNote.id}/memorize`);
        newNote.is_memorized = true;
        setSuccessMessage('Note created and memorized successfully!');
      } else {
        setSuccessMessage('Note created successfully!');
      }

      // Reset form and return to list
      setFormData({
        title: '',
        date: new Date().toISOString().split('T')[0],
        category: 'Technical Skills',
        content: ''
      });
      
      // Show success message briefly before returning to list
      const timeoutId = setTimeout(() => {
        setView('list');
        setSuccessMessage(null);
        loadNotes();
      }, 1500);
      timeoutRefs.current.push(timeoutId);
    } catch (err) {
      console.error('Error creating note:', err);
      setError(err.response?.data?.detail || 'Failed to create note');
    } finally {
      setLoading(false);
    }
  };

  const updateNote = async (memorize = false) => {
    if (!formData.title.trim() || !formData.content.trim()) {
      setError('Title and content are required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await api.put(`/api/notes/${currentNote.id}`, formData);
      const updatedNote = response.data;

      // If memorize is true, also memorize the note
      if (memorize && !updatedNote.is_memorized) {
        await api.post(`/api/notes/${updatedNote.id}/memorize`);
        updatedNote.is_memorized = true;
        setSuccessMessage('Note updated and memorized successfully!');
      } else if (memorize && updatedNote.is_memorized) {
        setSuccessMessage('Note updated successfully! (Already memorized)');
      } else {
        setSuccessMessage('Note updated successfully!');
      }

      setCurrentNote(null);
      setFormData({
        title: '',
        date: new Date().toISOString().split('T')[0],
        category: 'Technical Skills',
        content: ''
      });
      
      // Show success message briefly before returning to list
      const timeoutId = setTimeout(() => {
        setView('list');
        setSuccessMessage(null);
        loadNotes();
      }, 1500);
      timeoutRefs.current.push(timeoutId);
    } catch (err) {
      console.error('Error updating note:', err);
      setError(err.response?.data?.detail || 'Failed to update note');
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    setError(null);
    setSuccessMessage(null);

    try {
      await api.delete(`/api/notes/${noteId}`);
      setSuccessMessage('Note deleted successfully!');
      
      // Clear success message after delay
      const clearMsgTimeout = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      timeoutRefs.current.push(clearMsgTimeout);
      
      loadNotes();
      if (view === 'detail') {
        setView('list');
      }
    } catch (err) {
      console.error('Error deleting note:', err);
      setError(err.response?.data?.detail || 'Failed to delete note');
    }
  };

  const memorizeNote = async (noteId) => {
    setError(null);
    setSuccessMessage(null);

    try {
      await api.post(`/api/notes/${noteId}/memorize`);
      setSuccessMessage('Note memorized successfully!');
      
      // Clear success message after delay
      const clearMsgTimeout = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      timeoutRefs.current.push(clearMsgTimeout);
      
      loadNotes();
      if (currentNote && currentNote.id === noteId) {
        setCurrentNote({ ...currentNote, is_memorized: true });
      }
    } catch (err) {
      console.error('Error memorizing note:', err);
      setError(err.response?.data?.detail || 'Failed to memorize note');
    }
  };

  const searchNotes = async (query) => {
    if (!query.trim()) {
      loadNotes();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/api/notes/search?q=${encodeURIComponent(query)}`);
      // Backend returns { notes: [...], total: N, success: true }
      if (response.data && Array.isArray(response.data.notes)) {
        setNotes(response.data.notes);
      } else {
        setNotes([]);
      }
    } catch (err) {
      console.error('Error searching notes:', err);
      setError(err.response?.data?.detail || 'Failed to search notes');
    } finally {
      setLoading(false);
    }
  };

  // Memoized filtered and sorted notes
  const filteredNotes = useMemo(() => {
    let result = [...notes];

    // Apply date range filter
    if (dateRange.start || dateRange.end) {
      result = result.filter(note => {
        const noteDate = new Date(note.date);
        const startDate = dateRange.start ? new Date(dateRange.start) : null;
        const endDate = dateRange.end ? new Date(dateRange.end) : null;

        if (startDate && noteDate < startDate) return false;
        if (endDate && noteDate > endDate) return false;
        return true;
      });
    }

    // Apply sort order
    result.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return result;
  }, [notes, dateRange.start, dateRange.end, sortOrder]);

  const clearFilters = () => {
    setDateRange({ start: '', end: '' });
    setSortOrder('desc');
    setSearchQuery('');
    loadNotes(true);
  };

  const viewNote = (note) => {
    setCurrentNote(note);
    setView('detail');
  };

  const startEdit = (note) => {
    setCurrentNote(note);
    setFormData({
      title: note.title,
      date: note.date.split('T')[0],
      category: note.category,
      content: note.content
    });
    setView('edit');
  };

  const startCreate = () => {
    setCurrentNote(null);
    setFormData({
      title: '',
      date: new Date().toISOString().split('T')[0],
      category: 'Technical Skills',
      content: ''
    });
    setView('create');
  };

  const cancelForm = () => {
    setCurrentNote(null);
    setFormData({
      title: '',
      date: new Date().toISOString().split('T')[0],
      category: 'Technical Skills',
      content: ''
    });
    setError(null);
    setSuccessMessage(null);
    setView('list');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getContentPreview = (content) => {
    return content.length > 150 ? content.substring(0, 150) + '...' : content;
  };

  // Handle search input with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchNotes(searchQuery);
      } else {
        loadNotes(true);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Render functions
  const renderList = () => (
    <div className="notes-list-view">
      <div className="notes-header">
        <div className="notes-title">
          <BookOpen size={24} />
          <h2>Coach Notes</h2>
        </div>
        <div className="header-actions">
          <button
            onClick={() => setShowStatistics(!showStatistics)}
            className={`action-button ${showStatistics ? 'active' : 'secondary'}`}
          >
            <BarChart3 size={16} />
            {showStatistics ? 'Hide' : 'Show'} Statistics
          </button>
          <button onClick={startCreate} className="action-button">
            <Plus size={16} />
            New Note
          </button>
        </div>
      </div>

      {showStatistics && (
        <CoachNotesStatistics onClose={() => setShowStatistics(false)} />
      )}

      <div className="notes-controls">
        <div className="search-bar">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <div className="date-range-filter">
            <label>From:</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="date-input"
            />
            <label>To:</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="date-input"
            />
          </div>

          <div className="sort-control">
            <label>Sort:</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="sort-select"
            >
              <option value="desc">Most Recent</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

          {(dateRange.start || dateRange.end || searchQuery) && (
            <button
              onClick={clearFilters}
              className="clear-filters-button"
            >
              <X size={16} />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="success-message">
          <Save size={16} />
          {successMessage}
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <Loader className="spinner" size={32} />
          <p>Loading notes...</p>
        </div>
      ) : notes.length === 0 ? (
        <div className="empty-state">
          <BookOpen size={48} />
          <h3>No notes yet</h3>
          <p>Create your first coach note to get started</p>
          <button onClick={startCreate} className="generate-button">
            <Plus size={18} />
            Create Note
          </button>
        </div>
      ) : (
        <div className="notes-grid">
          {filteredNotes.map((note) => (
            <div key={note.id} className="note-card">
              <div className="note-card-header">
                <h3>{note.title}</h3>
                {note.is_memorized && (
                  <span className="memorized-badge">
                    <Star size={14} fill="currentColor" />
                    Memorized
                  </span>
                )}
              </div>
              <div className="note-card-meta">
                <span className="note-date">
                  <Calendar size={14} />
                  {formatDate(note.date)}
                </span>
                <span className="note-category">{note.category}</span>
              </div>
              <div className="note-card-content">
                <p>{getContentPreview(note.content)}</p>
              </div>
              <div className="note-card-actions">
                <button
                  onClick={() => viewNote(note)}
                  className="action-button"
                >
                  View
                </button>
                <button
                  onClick={() => startEdit(note)}
                  className="action-button secondary"
                >
                  <Edit size={16} />
                  Edit
                </button>
                <button
                  onClick={() => deleteNote(note.id)}
                  className="action-button danger"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load More Button */}
      {!loading && !searchQuery && hasMore && notes.length > 0 && (
        <div className="load-more-container">
          <button
            onClick={() => loadNotes(false)}
            className="load-more-button"
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <Loader className="spinner" size={16} />
                Loading...
              </>
            ) : (
              'Load More Notes'
            )}
          </button>
        </div>
      )}
    </div>
  );

  const renderForm = () => (
    <NoteForm
      mode={view}
      formData={formData}
      onFormChange={setFormData}
      onSave={() => view === 'create' ? createNote(false) : updateNote(false)}
      onMemorize={() => view === 'create' ? createNote(true) : updateNote(true)}
      onCancel={cancelForm}
      loading={loading}
      error={error}
      successMessage={successMessage}
    />
  );

  const renderDetail = () => {
    if (!currentNote) return null;

    return (
      <div className="note-detail-view">
        <div className="detail-header">
          <button onClick={() => setView('list')} className="back-button">
            <ArrowLeft size={18} />
            Back to Notes
          </button>
        </div>

        <div className="note-detail-card">
          <div className="note-detail-header">
            <div>
              <h2>{currentNote.title}</h2>
              <div className="note-detail-meta">
                <span className="note-date">
                  <Calendar size={16} />
                  {formatDate(currentNote.date)}
                </span>
                <span className="note-category">{currentNote.category}</span>
                {currentNote.is_memorized && (
                  <span className="memorized-badge">
                    <Star size={14} fill="currentColor" />
                    Memorized
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="note-detail-content">
            <ReactMarkdown>{currentNote.content}</ReactMarkdown>
          </div>

          <div className="note-detail-footer">
            <div className="note-timestamps">
              <p>Created: {formatDate(currentNote.created_at)}</p>
              <p>Last Updated: {formatDate(currentNote.updated_at)}</p>
            </div>
            <div className="note-detail-actions">
              <button
                onClick={() => startEdit(currentNote)}
                className="action-button"
              >
                <Edit size={16} />
                Edit
              </button>
              <button
                onClick={() => deleteNote(currentNote.id)}
                className="action-button danger"
              >
                <Trash2 size={16} />
                Delete
              </button>
              {!currentNote.is_memorized && (
                <button
                  onClick={() => memorizeNote(currentNote.id)}
                  className="action-button memorize-button"
                >
                  <Star size={16} />
                  Memorize
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="notes-tab">
      <div className="notes-content">
        {view === 'list' && renderList()}
        {(view === 'create' || view === 'edit') && renderForm()}
        {view === 'detail' && renderDetail()}
      </div>
    </div>
  );
};

export default NotesTab;
